import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { useUser } from "@/web/auth/useUser";

import { Button } from "@/web/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/web/components/primitives/dialog";
import { toaster } from "@/web/components/primitives/toast";
import {
  canSubmitPledge,
  EMPTY_PLEDGE_FORM_STATE,
  PledgeFormBody,
  type PledgeFormState,
} from "@/web/components/styled/PledgeForm";
import { GapiPlacesSearchBar, type AllNominationsRow } from "@/web/components/styled/GapiSearchBar";
import { ErrorHandler } from "@/web/lib/errors";
import { formatNumberToShort } from "@/web/lib/formatting";
import { geocodeAddress } from "@/web/lib/geocoding";
import { cn } from "@/web/lib/styling";
import { fetchPlaceDetailsFromPrediction } from "@/web/lib/googlePlaces/fetchPlaceFromPrediction";
import { getPlacePhotoUrls } from "@/web/lib/googlePlaces/photoUtils";
import {
  PlaceFormatters,
  placeDisplayName,
  placePrimaryTypeDisplayName,
} from "@/web/lib/googlePlaces/placeFormatters";
import { extractCardIdFromAlreadyOnPlatformError } from "@/web/lib/spacetimedbErrors";
import { tableQueryOptions, useProcedureMutation } from "@/web/lib/tanstack";
import {
  NominationPledgeStep,
  ShareableCardPledgeStep,
} from "@/web/routes/-nomination/NominatePledgeDialogs";
import { AuthnFormDialog } from "@/web/auth/AuthnFormDialog";
import { useFormNavigation } from "@/web/hooks/useFormNavigation";
import {
  formatNominateLocation,
  type NominatePlaceMatch,
  useNominatePlaceLookup,
} from "@/web/routes/-nomination/useNominatePlaceLookup";
import { ConfirmPlaceOverview } from "@/web/routes/_protected/smb/-onboarding/ConfirmPlaceOverview";
import {
  CardCustomizationCarousel,
  type CarouselSelection,
  type PlaceDisplayInfo,
} from "@/web/routes/_protected/smb/-onboarding/CustomizingStyle";
import { getTextColor } from "@/web/lib/colors";
import { toCanonicalAddress } from "@/web/routes/_protected/smb/-types";
import { procedures, tables } from "@tycho/web-bindings";

type NominateDialogProps = {
  trigger: React.ReactElement;
};

type NominateStage = "select" | "details" | "customize" | "existing" | "pledge";

type NominationDraft = {
  place: google.maps.places.Place;
  formState: PledgeFormState;
  carouselSelection: CarouselSelection | null;
  availablePhotos: number | undefined;
};

/** Whether the viewer already has a pledge on this match (drives copy). */
function matchHasExistingPledge(match: NominatePlaceMatch): boolean {
  if (match.kind === "nomination") return match.userInterestDollars !== undefined;
  if (match.kind === "onPlatform") return match.userInterest !== undefined;
  return false;
}

export function NominateDialog({ trigger }: NominateDialogProps) {
  const [open, setOpen] = useState(false);
  const { stage } = useFormNavigation<NominateStage>("select");
  const [draft, setDraft] = useState<NominationDraft | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<NominatePlaceMatch | null>(null);
  // Post-submit "create an account" prompt for anonymous pledgers. Hoisted to
  // the top level (sibling to the nominate dialog) so it survives the nominate
  // dialog closing on submit.
  const [authPrompt, setAuthPrompt] = useState<{ open: boolean; businessName: string }>({
    open: false,
    businessName: "",
  });
  const placeLookup = useNominatePlaceLookup();
  const user = useUser();
  const queryClient = useQueryClient();
  const { mutateAsync: createNominationHeroUploadUrl } = useProcedureMutation(
    procedures.createNominationHeroUploadUrl,
  );
  const { mutateAsync: submitNominationProcedure } = useProcedureMutation(
    procedures.submitNomination,
  );
  const updateCarouselSelection = useCallback((carouselSelection: CarouselSelection) => {
    setDraft((current) => (current ? { ...current, carouselSelection } : current));
  }, []);
  const updateAvailablePhotos = useCallback((availablePhotos: number | undefined) => {
    setDraft((current) => (current ? { ...current, availablePhotos } : current));
  }, []);
  const submitMutation = useMutation({
    mutationFn: async () => {
      const currentDraft = draft;
      const placeId = currentDraft?.place.id;
      const amount = currentDraft?.formState.amount;
      if (!currentDraft || !placeId || amount === undefined || amount <= 0) {
        throw new Error("Missing nomination details");
      }

      const { place, formState, carouselSelection } = currentDraft;
      const businessName = placeDisplayName(place);
      const businessType = PlaceFormatters.getBusinessCategoryType(place);
      const canonicalAddress = toCanonicalAddress(PlaceFormatters.getAddressParts(place));

      // The nominator who creates the row gets first-come-first-serve on the
      // hero and tint (see `submitNominationInTx`); later nominators cannot
      // replace them.
      let heroObjectPath: string | undefined;
      let colorPrimary: string | undefined;
      let colorText: string | undefined;
      if (carouselSelection?.selectedUrl) {
        try {
          heroObjectPath = await uploadNominationHeroImage({
            imageUrl: carouselSelection.selectedUrl,
            createUploadUrl: createNominationHeroUploadUrl,
          });
        } catch (uploadError) {
          ErrorHandler.reportError(uploadError);
          throw new Error("Couldn’t upload your image — please try again.");
        }
        colorPrimary = carouselSelection.selectedColor;
        colorText = getTextColor(carouselSelection.selectedColor);
      }

      // A geocoding failure is non-fatal; the admin backfill can fill it later.
      const coords = await geocodeAddress(canonicalAddress).catch((error: unknown) => {
        ErrorHandler.reportError(error);
        return null;
      });

      return submitNominationProcedure({
        googlePlaceId: placeId,
        name: businessName,
        businessType,
        canonicalAddress,
        intendedInvestmentDollars: amount,
        reason: formState.supportiveMessage.trim() || undefined,
        firstName: formState.firstName.trim() || undefined,
        lastName: formState.lastName.trim() || undefined,
        consentToDisplay: formState.consentToDisplay,
        heroObjectPath,
        colorPrimary,
        colorText,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.campaignBrowseNominations).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myNominationInterests).queryKey,
        }),
      ]);
      toaster({ message: "Nomination submitted", variant: "success" });
      resetAndClose();
    },
    onError: (error) => {
      const cardId = extractCardIdFromAlreadyOnPlatformError(error);
      if (cardId) {
        routeAlreadyOnPlatformFallback(cardId);
        return;
      }
      ErrorHandler.default(error);
    },
  });

  function resetAndClose() {
    setOpen(false);
    // Defer reset so the closing animation doesn't flash the step-1 UI.
    setTimeout(() => {
      stage.reset("select");
      setDraft(null);
      setSelectedMatch(null);
    }, 200);
  }

  function isLookupReady() {
    if (!placeLookup.isLoading) return true;
    toaster({ message: "Still checking for existing businesses. Please try again in a moment." });
    return false;
  }

  function routePlace(place: google.maps.places.Place) {
    if (!isLookupReady()) return;
    const match = place.id ? placeLookup.lookupByGooglePlaceId(place.id) : null;
    if (match) {
      setSelectedMatch(match);
      stage.push("existing");
      return;
    }
    setDraft({
      place,
      formState: {
        ...EMPTY_PLEDGE_FORM_STATE,
        firstName: user?.firstName?.trim() ?? "",
        lastName: user?.lastName?.trim() ?? "",
        email: user?.email ?? "",
      },
      carouselSelection: null,
      availablePhotos: undefined,
    });
    stage.push("details");
  }

  function routeNomination(nomination: AllNominationsRow) {
    if (!isLookupReady()) return;
    const match = placeLookup.lookupByGooglePlaceId(nomination.googlePlaceId) ?? {
      kind: "nomination" as const,
      nominationId: nomination.id,
      googlePlaceId: nomination.googlePlaceId,
      name: nomination.name ?? "this business",
      location: formatNominateLocation(nomination.canonicalAddress),
      totalInterested: nomination.totalInterested,
      totalInterestDollars: 0,
      heroCdnPath: nomination.heroCdnPath,
      userInterestDollars: undefined,
      userReason: undefined,
    };
    setSelectedMatch(match);
    stage.push("existing");
  }

  function routeAlreadyOnPlatformFallback(cardId: string) {
    setSelectedMatch(placeLookup.resolveCardMatchById(cardId));
    stage.replace("existing");
  }

  let title: string;
  // `null` renders no description row (and reclaims its vertical space).
  let description: string | null;
  if (stage.current === "select") {
    title = "Nominate a business";
    description = "Search for a business whose property you'd like to invest in.";
  } else if (stage.current === "details") {
    title = "Nominate and pledge";
    description = null;
  } else if (stage.current === "customize") {
    title = "Customize the card";
    description = null;
  } else if (stage.current === "pledge" && selectedMatch) {
    const isUpdate = matchHasExistingPledge(selectedMatch);
    title = isUpdate ? "Update your pledge" : "Make a pledge";
    description = isUpdate
      ? "Update your details below if you'd like to change your pledge."
      : null;
  } else if (selectedMatch?.kind === "nomination") {
    title = "Already nominated";
    description = "This business has already been nominated. Add or update your pledge instead.";
  } else {
    title = "Already on WITHUS";
    description =
      selectedMatch?.kind === "onPlatformViewOnly"
        ? "This business is already on WITHUS. Visit their page to learn more."
        : "This business is already on WITHUS. Add or update your pledge instead.";
  }

  const openAuthPrompt = (businessName: string) => setAuthPrompt({ open: true, businessName });

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setOpen(true);
          } else {
            resetAndClose();
          }
        }}
      >
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent
          className={cn(
            "max-w-130 gap-0 px-6 pt-5 pb-6 md:px-8 md:pt-6 md:pb-8",
            // The select step's autocomplete dropdown is absolutely positioned
            // and must spill past the dialog edges; a scroll/clip context would
            // cut it off. The other steps are long forms that should scroll
            // within the viewport on small screens.
            stage.current === "select"
              ? "overflow-visible"
              : "max-h-[90dvh] overflow-x-hidden overflow-y-auto",
          )}
          closeButtonClassName="top-5 right-6 md:top-6 md:right-8"
          // Steps without a description opt out of the a11y link so Radix
          // doesn't warn about a missing `DialogDescription`.
          {...(description === null ? { "aria-describedby": undefined } : {})}
        >
          <DialogHeader className="mb-4 pr-8">
            <DialogTitle className="withus-strong text-2xl">{title}</DialogTitle>
            {description !== null ? (
              <DialogDescription className="px-4 text-sm text-muted-foreground">
                {description}
              </DialogDescription>
            ) : null}
          </DialogHeader>

          {stage.current === "select" && (
            <SelectStep
              onSelected={routePlace}
              onSelectNomination={routeNomination}
              getPlaceAnnotation={placeLookup.getPlaceAnnotation}
              lookupLoading={placeLookup.isLoading}
            />
          )}

          {stage.current === "details" && draft && (
            <DetailsStep
              place={draft.place}
              formState={draft.formState}
              onFieldChange={(key, value) => {
                setDraft((current) =>
                  current
                    ? { ...current, formState: { ...current.formState, [key]: value } }
                    : current,
                );
              }}
              onBack={stage.pop}
              onContinue={() => stage.push("customize")}
            />
          )}

          {stage.current === "customize" && draft && (
            <CustomizationStep
              draft={draft}
              isSubmitting={submitMutation.isPending}
              onBack={stage.pop}
              onSelectionChange={updateCarouselSelection}
              onPhotosResolved={updateAvailablePhotos}
              onSubmit={() => submitMutation.mutate()}
            />
          )}

          {stage.current === "existing" && selectedMatch && (
            <ExistingBusinessStep
              match={selectedMatch}
              onBack={stage.pop}
              onClose={resetAndClose}
              onPledge={() => stage.push("pledge")}
            />
          )}

          {stage.current === "pledge" && selectedMatch?.kind === "nomination" && (
            <NominationPledgeStep
              nominatedBusinessId={selectedMatch.nominationId}
              businessName={selectedMatch.name}
              initialAmount={selectedMatch.userInterestDollars}
              initialReason={selectedMatch.userReason}
              initialFirstName={selectedMatch.userFirstName}
              initialLastName={selectedMatch.userLastName}
              initialConsentToDisplay={selectedMatch.userConsentToDisplay}
              onBack={stage.pop}
              onSubmitted={resetAndClose}
              onAnonymousPledge={() => openAuthPrompt(selectedMatch.name)}
            />
          )}

          {stage.current === "pledge" && selectedMatch?.kind === "onPlatform" && (
            <ShareableCardPledgeStep
              cardId={selectedMatch.cardId}
              businessName={selectedMatch.name}
              initialInterest={selectedMatch.userInterest}
              onBack={stage.pop}
              onSubmitted={resetAndClose}
              onAnonymousPledge={() => openAuthPrompt(selectedMatch.name)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AuthnFormDialog
        open={authPrompt.open}
        onOpenChange={(next) => setAuthPrompt((prev) => ({ ...prev, open: next }))}
        title="Stay in the loop"
        description={`Keep up to date with ${authPrompt.businessName}'s progress by creating a WITH.CO account.`}
      />
    </>
  );
}

function SelectStep({
  onSelected,
  onSelectNomination,
  getPlaceAnnotation,
  lookupLoading,
}: {
  onSelected: (place: google.maps.places.Place) => void;
  onSelectNomination: (nomination: AllNominationsRow) => void;
  getPlaceAnnotation: (placeId: string) => string | undefined;
  lookupLoading: boolean;
}) {
  const handleSelectPrediction = async (
    prediction: google.maps.places.PlacePrediction,
  ): Promise<void> => {
    if (lookupLoading) {
      toaster({ message: "Still checking for existing businesses. Please try again in a moment." });
      return;
    }
    try {
      const fetched = await fetchPlaceDetailsFromPrediction(prediction);
      if (!fetched?.id) {
        toaster({ message: "Unable to fetch place info", variant: "error" });
        return;
      }
      onSelected(fetched);
    } catch (error) {
      ErrorHandler.default(error);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <GapiPlacesSearchBar
        onSelectPrediction={handleSelectPrediction}
        onSelectNomination={onSelectNomination}
        disabled={lookupLoading}
        getPlaceAnnotation={getPlaceAnnotation}
        placeholder="Search for a business..."
        className="mx-0 w-full"
      />
      {lookupLoading ? (
        <p className="text-xs text-muted-foreground">
          Checking for existing nominations and WITHUS pages…
        </p>
      ) : null}
    </div>
  );
}

function ExistingBusinessStep({
  match,
  onBack,
  onClose,
  onPledge,
}: {
  match: NominatePlaceMatch;
  onBack: () => void;
  onClose: () => void;
  onPledge: () => void;
}) {
  const isViewOnly = match.kind === "onPlatformViewOnly";
  const hasExistingPledge =
    match.kind === "nomination"
      ? match.userInterestDollars !== undefined
      : match.kind === "onPlatform"
        ? match.userInterest?.interestDollarAmount !== undefined
        : false;
  const interestedLabel =
    match.kind === "onPlatformViewOnly"
      ? null
      : formatNumberToShort(match.totalInterested, { showThousands: true });

  return (
    <div className="flex flex-col gap-4">
      <div className="border bg-withus-pure-white p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="border border-withus-dark-blue/20 px-2 py-1 text-xs font-semibold tracking-widest text-withus-dark-blue uppercase">
            {match.kind === "nomination" ? "Nominated" : "On WITHUS"}
          </span>
          {match.kind === "onPlatform" || match.kind === "onPlatformViewOnly" ? (
            <a
              href={`/ttw/${match.cardId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-withus-blue underline underline-offset-2 hover:no-underline"
            >
              View their page
            </a>
          ) : null}
        </div>
        <h3 className="withus-strong text-xl text-withus-dark-blue">{match.name}</h3>
        {match.kind !== "onPlatformViewOnly" && match.location ? (
          <p className="mt-1 text-sm text-muted-foreground">{match.location}</p>
        ) : null}
        {match.kind === "nomination" && match.nominatedByName ? (
          <p className="mt-2 text-sm text-muted-foreground">Nominated by {match.nominatedByName}</p>
        ) : null}
        {isViewOnly ? null : (
          <p className="mt-4 text-sm text-withus-dark-blue/80">
            {match.kind === "nomination"
              ? `This business has already been nominated — ${interestedLabel} interested. Add your pledge?`
              : `This business is already on WITHUS — ${interestedLabel} pledged. Add your pledge?`}
          </p>
        )}
      </div>

      <div className="flex justify-between gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onBack}>
          Nominate a different business
        </Button>
        {isViewOnly ? (
          <Button type="button" color="blue" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button type="button" color="blue" onClick={onPledge}>
            {hasExistingPledge ? "Update pledge" : "Add pledge"}
          </Button>
        )}
      </div>
    </div>
  );
}

async function putToSignedUrl(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  contentLengthRange: string,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "content-type": contentType,
      "x-goog-content-length-range": contentLengthRange,
    },
    body: blob,
  });
  if (!response.ok) {
    const text = await response.text().catch((error: unknown) => {
      ErrorHandler.reportError(error);
      return "";
    });
    throw new Error(`GCS upload failed (${response.status}): ${text}`);
  }
}

async function uploadNominationHeroImage(args: {
  imageUrl: string;
  createUploadUrl: (variables: { contentType: string }) => Promise<{
    uploadUrl: string;
    objectPath: string;
    contentType: string;
    contentLengthRange: string;
  }>;
}): Promise<string> {
  const response = await fetch(args.imageUrl);
  if (!response.ok) throw new Error(`Failed to fetch place photo (${response.status})`);

  const blob = await response.blob();
  const contentType = blob.type || undefined;
  if (!contentType) throw new Error("Unable to determine place photo content type");
  const { uploadUrl, objectPath, contentLengthRange } = await args.createUploadUrl({ contentType });
  await putToSignedUrl(uploadUrl, blob, contentType, contentLengthRange);
  return objectPath;
}

function DetailsStep({
  place,
  formState,
  onFieldChange,
  onBack,
  onContinue,
}: {
  place: google.maps.places.Place;
  formState: PledgeFormState;
  onFieldChange: <K extends keyof PledgeFormState>(key: K, value: PledgeFormState[K]) => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const businessName = placeDisplayName(place);
  const canContinue = Boolean(place.id) && canSubmitPledge(formState, true);

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <ConfirmPlaceOverview place={place} websiteUrl="" />
      <PledgeFormBody
        formState={formState}
        onFieldChange={onFieldChange}
        isPending={false}
        // Nomination submission is authed-only, so the anonymous email field
        // never renders here.
        isSignedIn
        businessName={businessName}
        idPrefix="nominate"
        amountLabel="Pledge amount"
        messageLabel="What makes this place worth investing in? (optional)"
        messagePlaceholder="Add a message"
      />

      <div className="flex flex-wrap justify-between gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button type="button" color="blue" onClick={onContinue} disabled={!canContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
}

function CustomizationStep({
  draft,
  isSubmitting,
  onBack,
  onSelectionChange,
  onPhotosResolved,
  onSubmit,
}: {
  draft: NominationDraft;
  isSubmitting: boolean;
  onBack: () => void;
  onSelectionChange: (selection: CarouselSelection) => void;
  onPhotosResolved: (availablePhotos: number | undefined) => void;
  onSubmit: () => void;
}) {
  const businessName = placeDisplayName(draft.place);
  const placeInfo: PlaceDisplayInfo = useMemo(
    () => ({
      displayName: businessName,
      cityState: PlaceFormatters.getCityState(draft.place) ?? "",
      primaryTypeDisplayName: placePrimaryTypeDisplayName(draft.place) ?? "Business",
      websiteUri: draft.place.websiteUri ?? "",
      photos: getPlacePhotoUrls(draft.place, 4),
    }),
    [draft.place, businessName],
  );
  const placeHasPhotos = placeInfo.photos.length > 0;
  // If extraction failed for every image, fall back to the default card rather
  // than trapping the nominator on this step.
  const canBypassImageStep = !placeHasPhotos || draft.availablePhotos === 0;
  const canSubmit =
    !isSubmitting && (canBypassImageStep || Boolean(draft.carouselSelection?.selectedUrl));

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-withus-dark-blue">
            Choose how this business shows up
          </h2>
          <p className="text-xs text-withus-dark-blue/80">
            Pick a photo and a tint for the nomination card. The first person to nominate a business
            picks its look — make it count.
          </p>
        </div>
        {placeHasPhotos && draft.availablePhotos !== 0 ? (
          <CardCustomizationCarousel
            place={placeInfo}
            placeIsLoading={false}
            allowCustomImage={false}
            badge={{ kind: "nominated" }}
            onChange={onSelectionChange}
            onPhotosResolved={onPhotosResolved}
          />
        ) : (
          <p className="text-xs text-withus-dark-blue/80">
            {placeHasPhotos
              ? "We couldn’t prepare an image preview for this business, so it’ll appear with a default look until an owner claims it. You can still submit your nomination."
              : "No photos were found for this business. It’ll appear with a default look until an owner claims it."}
          </p>
        )}
      </section>

      <div className="flex flex-wrap justify-between gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          type="button"
          color="blue"
          onClick={onSubmit}
          disabled={!canSubmit}
          isLoading={isSubmitting}
        >
          Submit nomination
        </Button>
      </div>
    </div>
  );
}
